import { createContext, useRef, useState, useMemo, useEffect } from 'react';
import { INITIAL_TOOL_STATE } from 'constants/tools';
import { FONT_DEFAULT_SIZE } from 'constants/fonts';
import { FILTER_PRESETS } from 'constants/filters';

export const CanvasContext = createContext();

export function CanvasProvider({ children }) {
  const [activeTool, setActiveTool] = useState(INITIAL_TOOL_STATE);
  const [textItems, setTextItems] = useState([]); // list of { x, y, text, ... }

  // Stickers (HTML overlays for now; export can be added later)
  const [stickerItems, setStickerItems] = useState([]); // list of { id, src, x, y, w, h, rotation }
  const [selectedStickerId, setSelectedStickerId] = useState(null);
  const [selectedStickerSrc, setSelectedStickerSrc] = useState(null);

  const [redrawTrigger, setRedrawTrigger] = useState(0);
  const [pendingExitTool, setPendingExitTool] = useState(null);
  const [fontSize, setFontSize] = useState(FONT_DEFAULT_SIZE);
  const [fontScaleRatio, setFontScaleRatio] = useState(1);
  const [canvasScale, setCanvasScale] = useState(1);
  // Adjustments
  const [brightness, setBrightness] = useState(1);
  const [contrast, setContrast] = useState(1);
  const [saturation, setSaturation] = useState(1);
  const [hueRotate, setHueRotate] = useState(0);
  const [blur, setBlur] = useState(0);
  const [textInsertPoint, setTextInsertPoint] = useState(null);
  const [stickerInsertPoint, setStickerInsertPoint] = useState(null);
  // Transform mode
  const [transformMode, setTransformMode] = useState('home');

  // Rotate preview (CSS-only while sliding)
  const [rotatePreviewDeg, setRotatePreviewDeg] = useState(0);

  // track whole-canvas rotation (Rotate tool should update this)
  const [canvasRotationDeg, setCanvasRotationDeg] = useState(0);

  // --- Unapplied rotate guard (modal) ---
  const [rotateGuardOpen, setRotateGuardOpen] = useState(false);
  const rotateGuardActionRef = useRef(null);

  // Undo/Redo
  const MAX_HISTORY = 20;

  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const hasUnappliedRotate =
    activeTool === 'transform' &&
    transformMode === 'rotate' &&
    rotatePreviewDeg !== 0;

  /**
   * Request to leave rotate while a preview rotation is not applied.
   * If there's no unapplied rotation, runs the action immediately.
   */
  const requestRotateExit = (action) => {
    if (!hasUnappliedRotate) {
      action?.();
      return true;
    }
    rotateGuardActionRef.current = typeof action === 'function' ? action : null;
    setRotateGuardOpen(true);
    return false;
  };

  const closeRotateGuard = () => {
    setRotateGuardOpen(false);
    rotateGuardActionRef.current = null;
  };

  const confirmRotateGuardDiscard = () => {
    // Discard preview rotation
    setRotatePreviewDeg(0);
    const action = rotateGuardActionRef.current;
    closeRotateGuard();
    action?.();
  };

  // Preset filters: single source of truth
  // null | 'blackAndWhite' | 'vintage' | 'yourNextFilter' | ...
  const [activeFilter, setActiveFilter] = useState(FILTER_PRESETS.NONE);

  // Derived booleans for existing code (ImageActionButton, etc.)
  const isBlackAndWhite = activeFilter === FILTER_PRESETS.BLACK_AND_WHITE;
  const isVintage = activeFilter === FILTER_PRESETS.VINTAGE;
  const isWarmGlow = activeFilter === FILTER_PRESETS.WARM_GLOW;
  const isCoolTone = activeFilter === FILTER_PRESETS.COOL_TONE;
  const isFadedFilm = activeFilter === FILTER_PRESETS.FADED_FILM;
  const isSepia = activeFilter === FILTER_PRESETS.SEPIA;
  const isSoftPastel = activeFilter === FILTER_PRESETS.SOFT_PASTEL;
  const isVibrantPop = activeFilter === FILTER_PRESETS.VIBRANT_POP;
  const isCinematic = activeFilter === FILTER_PRESETS.CINEMATIC;
  const isNaturalEnhance = activeFilter === FILTER_PRESETS.NATURAL_ENHANCE;
  const isBright = activeFilter === FILTER_PRESETS.BRIGHT;
  const isClear = activeFilter === FILTER_PRESETS.CLEAR;

  // refs
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const overlayRef = useRef(null); // ← overlay above the canvas
  const hasAddedTextRef = useRef(false);
  const originalCanvasWidthRef = useRef(null);

  const canvasRectRef = useRef({ left: 0, top: 0 });
  const scaleRef = useRef({ x: 1, y: 1 }); // canvas px per CSS px

  const inputRefs = useRef([]);

  const canvasFilter = useMemo(() => {
    const BLUR_PREVIEW_MULTIPLIER = 0.2;

    const parts = [
      `brightness(${brightness})`,
      `contrast(${contrast})`,
      `saturate(${saturation})`,
      `hue-rotate(${hueRotate}deg)`,
      `blur(${blur * BLUR_PREVIEW_MULTIPLIER}px)`,
    ];

    if (isBlackAndWhite) parts.push('grayscale(1)');
    if (isVintage) {
      parts.push(
        'sepia(0.35)',
        'contrast(1.05)',
        'brightness(1.02)',
        'saturate(0.9)',
        'hue-rotate(-5deg)',
      );
    }
    if (isWarmGlow) {
      parts.push(
        'brightness(1.05)',
        'contrast(0.98)',
        'saturate(1.15)',
        'hue-rotate(8deg)',
        'sepia(0.15)',
      );
    }
    if (isCoolTone) {
      parts.push(
        'brightness(1.0)',
        'contrast(1.07)',
        'saturate(0.76)',
        'hue-rotate(-34deg)',
      );
    }
    if (isFadedFilm) {
      parts.push(
        'contrast(0.75)',
        'brightness(1.07)',
        'saturate(0.74)',
        'sepia(0.12)',
        'hue-rotate(-2deg)',
      );
    }
    if (isSepia) {
      parts.push(
        'sepia(1)',
        'saturate(1.12)',
        'hue-rotate(-6deg)',
        'contrast(1.03)',
        'brightness(1.02)',
      );
    }
    if (isSoftPastel) {
      parts.push(
        'brightness(1.09)',
        'contrast(0.79)',
        'saturate(0.69)',
        'hue-rotate(-6deg)',
        'sepia(0.06)',
      );
    }
    if (isVibrantPop) {
      parts.push(
        'brightness(1.06)',
        'contrast(1.08)',
        'saturate(1.28)',
        'hue-rotate(2deg)',
      );
    }
    if (isCinematic) {
      parts.push(
        'contrast(1.14)',
        'brightness(0.98)',
        'saturate(0.92)',
        'hue-rotate(-8deg)',
      );
    }
    if (isNaturalEnhance) {
      parts.push('contrast(1.05)', 'saturate(1.12)', 'brightness(1.02)');
    }
    if (isBright) {
      parts.push('brightness(1.12)', 'contrast(1.03)');
    }
    if (isClear) {
      parts.push('contrast(1.08)', 'saturate(1.05)', 'brightness(1.01)');
    }

    return parts.join(' ');
  }, [
    brightness,
    contrast,
    saturation,
    hueRotate,
    blur,
    isBlackAndWhite,
    isVintage,
    isWarmGlow,
    isCoolTone,
    isFadedFilm,
    isSepia,
    isSoftPastel,
    isVibrantPop,
    isCinematic,
    isNaturalEnhance,
    isBright,
    isClear,
  ]);

  useEffect(() => {
    // If user leaves Transform, always reset transformMode
    if (activeTool !== 'transform' && transformMode !== 'home') {
      setTransformMode('home');
    }
  }, [activeTool, transformMode, setTransformMode]);

  const bumpRedraw = () => setRedrawTrigger((t) => t + 1);

  const createHistorySnapshot = () => {
    const canvas = canvasRef.current;

    return {
      canvasDataUrl: canvas ? canvas.toDataURL('image/png') : null,
      canvasWidth: canvas?.width || null,
      canvasHeight: canvas?.height || null,
      canvasStyleWidth: canvas?.style?.width || '',
      canvasStyleHeight: canvas?.style?.height || '',

      textItems,
      stickerItems,
      selectedStickerId,
      canvasRotationDeg,

      brightness,
      contrast,
      saturation,
      hueRotate,
      blur,
      activeFilter,
    };
  };

  const saveHistory = () => {
    const snapshot = createHistorySnapshot();

    setUndoStack((prev) => {
      const next = [...prev, snapshot];

      if (next.length > MAX_HISTORY) {
        return next.slice(next.length - MAX_HISTORY);
      }

      return next;
    });

    setRedoStack([]);
  };

  const restoreHistorySnapshot = (snapshot) => {
    if (!snapshot) return;

    const canvas = canvasRef.current;
    if (!canvas || !snapshot.canvasDataUrl) return;

    const img = new Image();

    img.onload = () => {
      canvas.width = snapshot.canvasWidth;
      canvas.height = snapshot.canvasHeight;
      canvas.style.width = snapshot.canvasStyleWidth;
      canvas.style.height = snapshot.canvasStyleHeight;

      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;

      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);

      setTextItems(snapshot.textItems || []);
      setStickerItems(snapshot.stickerItems || []);
      setSelectedStickerId(snapshot.selectedStickerId || null);
      setCanvasRotationDeg(snapshot.canvasRotationDeg || 0);

      setBrightness(snapshot.brightness ?? 1);
      setContrast(snapshot.contrast ?? 1);
      setSaturation(snapshot.saturation ?? 1);
      setHueRotate(snapshot.hueRotate ?? 0);
      setBlur(snapshot.blur ?? 0);
      setActiveFilter(snapshot.activeFilter ?? FILTER_PRESETS.NONE);

      bumpRedraw();
    };

    img.src = snapshot.canvasDataUrl;
  };

  const undo = () => {
    if (undoStack.length === 0) return;

    const snapshotToRestore = undoStack[undoStack.length - 1];
    const remainingUndoStack = undoStack.slice(0, -1);
    const currentSnapshot = createHistorySnapshot();

    setUndoStack(remainingUndoStack);

    setRedoStack((prevRedoStack) => {
      const next = [...prevRedoStack, currentSnapshot];

      if (next.length > MAX_HISTORY) {
        return next.slice(next.length - MAX_HISTORY);
      }

      return next;
    });

    restoreHistorySnapshot(snapshotToRestore);
  };

  const redo = () => {
    if (redoStack.length === 0) return;

    const snapshotToRestore = redoStack[redoStack.length - 1];
    const remainingRedoStack = redoStack.slice(0, -1);
    const currentSnapshot = createHistorySnapshot();

    setRedoStack(remainingRedoStack);

    setUndoStack((prevUndoStack) => {
      const next = [...prevUndoStack, currentSnapshot];

      if (next.length > MAX_HISTORY) {
        return next.slice(next.length - MAX_HISTORY);
      }

      return next;
    });

    restoreHistorySnapshot(snapshotToRestore);
  };

  // ✅ UPDATED: default sticker size is now based on visible canvas size via canvasScale
  const addSticker = ({
    src,
    x,
    y,
    w,
    h,
    rotation = 0,
    flipX = false,
    flipY = false,
  }) => {
    if (!src) return;

    saveHistory();

    // canvasScale is CSSpx-per-canvaspx (set in Canvas: setCanvasScale(canvasRect.width / canvas.width))
    const safeCanvasScale =
      typeof canvasScale === 'number' && canvasScale > 0 ? canvasScale : 1;

    const isMobile =
      typeof window !== 'undefined' && (window.innerWidth || 0) < 768;

    // Target visual size (CSS px)
    const targetCssPx = isMobile ? 120 : 110;

    // Convert desired CSS px -> canvas px
    const defaultCanvasPx = Math.round(targetCssPx / safeCanvasScale);

    const finalW = typeof w === 'number' && w > 0 ? w : defaultCanvasPx;
    const finalH = typeof h === 'number' && h > 0 ? h : defaultCanvasPx;

    const id =
      typeof crypto !== 'undefined' && crypto?.randomUUID
        ? crypto.randomUUID()
        : `st_${Date.now()}_${Math.random().toString(16).slice(2)}`;

    setStickerItems((items) => [
      ...(Array.isArray(items) ? items : []),
      {
        id,
        src,
        x,
        y,
        w: finalW,
        h: finalH,
        rotation,
        flipX,
        flipY,
      },
    ]);

    setSelectedStickerId(id);
  };

  const deleteSelectedSticker = () => {
    setStickerItems((items) =>
      (Array.isArray(items) ? items : []).filter(
        (s) => s?.id !== selectedStickerId,
      ),
    );
    setSelectedStickerId(null);
  };

  const normaliseDeg = (deg) => {
    let v = Number(deg) || 0;
    v %= 360;
    if (v < 0) v += 360;
    return v;
  };

  // Keep stickers aligned with canvas rotate/flip operations
  // NOTE: sticker x/y are stored as the CENTRE in canvas px, same convention as text.
  const rotateStickerCentres90 = (dir, srcW, srcH) => {
    setStickerItems((items) =>
      (Array.isArray(items) ? items : []).map((s) => {
        if (!s) return s;

        const x = typeof s.x === 'number' ? s.x : 0;
        const y = typeof s.y === 'number' ? s.y : 0;
        const w = typeof s.w === 'number' ? s.w : 0;
        const h = typeof s.h === 'number' ? s.h : 0;
        const rotation = typeof s.rotation === 'number' ? s.rotation : 0;

        const next = dir === 1 ? { x: srcH - y, y: x } : { x: y, y: srcW - x };

        return {
          ...s,
          ...next,
          w: h,
          h: w,

          // rotate sticker orientation with the canvas
          rotation: normaliseDeg(rotation + dir * 90),

          // when rotating 90°, horizontal/vertical flip axes swap
          flipX: Boolean(s.flipY),
          flipY: Boolean(s.flipX),
        };
      }),
    );
  };

  const flipStickerCentres = (axis, w, h) => {
    setStickerItems((items) =>
      (Array.isArray(items) ? items : []).map((s) => {
        if (!s) return s;

        const x = typeof s.x === 'number' ? s.x : 0;
        const y = typeof s.y === 'number' ? s.y : 0;

        return {
          ...s,
          x: axis === 'h' ? w - x : x,
          y: axis === 'v' ? h - y : y,

          // preserve sticker location AND mirror its visual orientation
          flipX: axis === 'h' ? !Boolean(s.flipX) : Boolean(s.flipX),
          flipY: axis === 'v' ? !Boolean(s.flipY) : Boolean(s.flipY),
        };
      }),
    );
  };

  const rotateWholeCanvas90 = (dir = 1) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    saveHistory();

    // Discard any image-rotate preview so we don't have confusing states
    setRotatePreviewDeg(0);

    const srcW = canvas.width;
    const srcH = canvas.height;

    // Capture CURRENT displayed size before changing intrinsic canvas dimensions.
    // This matters after crop because CropTool hard-sets canvas.style.width/height.
    const rect = canvas.getBoundingClientRect();
    const displayW = rect.width;
    const displayH = rect.height;

    const temp = document.createElement('canvas');
    temp.width = srcH;
    temp.height = srcW;

    const tctx = temp.getContext('2d');
    if (!tctx) return;

    tctx.save();
    if (dir === 1) {
      // clockwise
      tctx.translate(temp.width, 0);
      tctx.rotate(Math.PI / 2);

      setCanvasRotationDeg((prev) => {
        let rot = prev + 90;
        if (rot === 360) rot = 0;
        return rot;
      });
    } else {
      // counter-clockwise
      tctx.translate(0, temp.height);
      tctx.rotate(-Math.PI / 2);

      setCanvasRotationDeg((prev) => {
        let rot = prev - 90;
        if (rot === -360) rot = 0;
        return rot;
      });
    }

    tctx.drawImage(canvas, 0, 0);
    tctx.restore();

    // Resize live canvas and draw result
    canvas.width = temp.width;
    canvas.height = temp.height;

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(temp, 0, 0);

    // IMPORTANT:
    // If CropTool has explicitly sized the canvas element with CSS,
    // swap that CSS box too so the rotated result doesn't get squished.
    if (displayW && displayH) {
      canvas.style.width = `${displayH}px`;
      canvas.style.height = `${displayW}px`;
      setCanvasScale(displayH / canvas.width);
    }

    // Rotate text overlays too.
    setTextItems((items) =>
      (Array.isArray(items) ? items : []).map((it) => {
        if (!it) return it;

        const x = typeof it.x === 'number' ? it.x : 0;
        const y = typeof it.y === 'number' ? it.y : 0;

        const width = typeof it.width === 'number' ? it.width : 0;
        const height = typeof it.height === 'number' ? it.height : 0;

        const rot = typeof it.rotation === 'number' ? it.rotation : 0;

        const next = dir === 1 ? { x: srcH - y, y: x } : { x: y, y: srcW - x };

        return {
          ...it,
          ...next,
          width,
          height,
          rotation: rot,
          flipX: Boolean(it.flipY),
          flipY: Boolean(it.flipX),
        };
      }),
    );

    rotateStickerCentres90(dir, srcW, srcH);

    bumpRedraw();
  };

  const flipWholeCanvas = (axis = 'h') => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    saveHistory();

    setRotatePreviewDeg(0);

    const w = canvas.width;
    const h = canvas.height;

    const temp = document.createElement('canvas');
    temp.width = w;
    temp.height = h;

    const tctx = temp.getContext('2d');
    if (!tctx) return;

    tctx.save();
    if (axis === 'h') {
      tctx.translate(w, 0);
      tctx.scale(-1, 1);
    } else {
      tctx.translate(0, h);
      tctx.scale(1, -1);
    }
    tctx.drawImage(canvas, 0, 0);
    tctx.restore();

    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(temp, 0, 0);

    setTextItems((items) =>
      (Array.isArray(items) ? items : []).map((it) => {
        if (!it) return it;

        const x = typeof it.x === 'number' ? it.x : 0;
        const y = typeof it.y === 'number' ? it.y : 0;

        const nextX = axis === 'h' ? w - x : x;
        const nextY = axis === 'v' ? h - y : y;

        return {
          ...it,
          x: nextX,
          y: nextY,
          flipX: axis === 'h' ? !it.flipX : it.flipX,
          flipY: axis === 'v' ? !it.flipY : it.flipY,
        };
      }),
    );

    // ✅ keep stickers aligned too
    flipStickerCentres(axis, w, h);

    bumpRedraw();
  };

  const rotateCanvasCW = () => rotateWholeCanvas90(1);
  const rotateCanvasCCW = () => rotateWholeCanvas90(-1);
  const flipCanvasHorizontal = () => flipWholeCanvas('h');
  const flipCanvasVertical = () => flipWholeCanvas('v');

  return (
    <CanvasContext.Provider
      value={{
        canvasRef,
        overlayRef,
        imageRef,
        activeTool,
        setActiveTool,
        textItems,
        setTextItems,

        // stickers
        stickerItems,
        setStickerItems,
        selectedStickerId,
        setSelectedStickerId,
        selectedStickerSrc,
        setSelectedStickerSrc,
        addSticker,
        deleteSelectedSticker,

        redrawTrigger,
        setRedrawTrigger,
        pendingExitTool,
        setPendingExitTool,
        canvasRectRef,
        scaleRef,
        hasAddedTextRef,
        inputRefs,
        fontSize,
        setFontSize,
        originalCanvasWidthRef,
        fontScaleRatio,
        setFontScaleRatio,
        canvasScale,
        setCanvasScale,

        brightness,
        setBrightness,
        contrast,
        setContrast,
        saturation,
        setSaturation,
        hueRotate,
        setHueRotate,
        blur,
        setBlur,
        canvasFilter,

        activeFilter,
        setActiveFilter,
        isBlackAndWhite,
        isVintage,
        isWarmGlow,
        isCoolTone,
        isFadedFilm,
        isSepia,
        isSoftPastel,
        isVibrantPop,
        isCinematic,
        isNaturalEnhance,
        isBright,
        isClear,

        transformMode,
        setTransformMode,
        rotatePreviewDeg,
        setRotatePreviewDeg,

        // rotate guard
        hasUnappliedRotate,
        rotateGuardOpen,
        requestRotateExit,
        closeRotateGuard,
        confirmRotateGuardDiscard,

        // undo/redo
        undoStack,
        redoStack,
        saveHistory,
        undo,
        redo,

        // canvas rotate/flip
        rotateCanvasCW,
        rotateCanvasCCW,
        flipCanvasHorizontal,
        flipCanvasVertical,

        textInsertPoint,
        setTextInsertPoint,
        stickerInsertPoint,
        setStickerInsertPoint,

        canvasRotationDeg,
        setCanvasRotationDeg,
      }}
    >
      {children}
    </CanvasContext.Provider>
  );
}
